import { useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../../atoms/Button/Button";
import { homeSchema } from "./home.schema";
import HomeBannerSection from "../../components/HomeBannerSection/HomeBannerSection";
import styles from "./Home.module.scss";
import Accordion from "../../components/Accordian/Accordian";
import HomeCardsSection from "../../components/HomeCardsSection/HomeCardsSection";
import HomeSliderSection from "../../components/HomeaSliderSection/HomeaSliderSection";
import { ApiService } from "../../services/apiServices";
import {
  Headings,
  Page,
  Sections,
  StorageEnums,
} from "../../enums/storageEnums";
import { toast } from "react-toastify";
import GlobalLoader from "../../atoms/GlobalLodaer/GlobalLoader";
import { getSignedUrl, uploadImage } from "../../constants/commonFunctions";

export interface APIResponse {
  id: string;
  section: string;
  key: string;
  content: string;
  page: string;
  order: number;
  isPublish: boolean;
}

const Home = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingForPublish, setLoadingForPublish] = useState<boolean>(false);
  const [isFormEdited, setIsFormEdited] = useState<boolean>(false);
  const [homePageData, setHomePageData] = useState<APIResponse[]>([]);
  const [isPublish, setIsPublish] = useState<boolean>(true);
  const apiService = new ApiService();
  const localStorageUser = JSON.parse(
    localStorage.getItem(StorageEnums.CREDENTIALS) || '{"accessToken": ""}'
  );

  const methods = useForm({
    resolver: yupResolver(homeSchema),
  });
  const { handleSubmit, formState } = methods;

  const CloudFrontURL = process.env.REACT_APP_CLOUDFRONT_URL;

  useEffect(() => {
    // Listen for changes in any form field
    setIsFormEdited(formState.isDirty);
  }, [formState]);

  const getHomePageData = async () => {
    const payload = {
      url: `pages/${Page.HOME_PAGE}`,
      headerToken: localStorageUser.data.accessToken,
    };
    const response = await apiService.get(payload);
    const data = response?.data?.result;
    setHomePageData(data);

    if (data.length) {
      setIsPublish(data[0].isPublish);
      const resetObject: { [key: string]: any } = {};
      data.forEach((item: any) => {
        resetObject[item.key] = item.content;
      });
      methods.reset(resetObject);
    } else {
      setIsPublish(true);
    }
  };

  useEffect(() => {
    getHomePageData();
  }, []);

  const handleSave = async (data: FieldValues) => {
    const { homeBannerImage, card1Icon, card2Icon, card3Icon } = data;

    const imagesToUpload = [
      { type: "homeBannerImage", image: homeBannerImage },
      { type: "card1Icon", image: card1Icon },
      { type: "card2Icon", image: card2Icon },
      { type: "card3Icon", image: card3Icon },
    ];

    if (!homePageData.length) {
      try {
        setLoading(true);

        // Generate signed URLs for each image
        const signedUrls = await Promise.all(
          imagesToUpload.map(async (imageObject) => {
            const { image } = imageObject;
            const imageInfo = {
              fileName: image.name,
              contentType: image.type,
            };
            const res = await getSignedUrl(imageInfo);
            return {
              type: imageObject.type,
              uploadUrl: res.data.uploadUrl,
              key: res.data.key,
            };
          })
        );

        // Prepare data to upload
        const dataToUpload: any[] = [
          // Banner section
          {
            section: Sections.HOME_BANNER_SECTION_1,
            key: Headings.HOME_BANNER_TITLE,
            content: data.homeBannerTitle,
            page: Page.HOME_PAGE,
          },
          {
            section: Sections.HOME_BANNER_SECTION_1,
            key: Headings.HOME_BANNER_DESCRIPTIION,
            content: data.homeBannerDescription,
            page: Page.HOME_PAGE,
          },
          {
            section: Sections.HOME_BANNER_SECTION_1,
            key: Headings.HOME_BANNER_IMAGE,
            content:
              CloudFrontURL +
                signedUrls.find((url) => url.type === "homeBannerImage")?.key ||
              "",
            page: Page.HOME_PAGE,
          },
          // Section 2 title and description
          {
            section: Sections.HOME_BANNER_SECTION_2,
            key: Headings.HOME_SECTION2_MAIN_TITLE,
            content: data.cardSectionMainTitle,
            page: Page.HOME_PAGE,
          },
          {
            section: Sections.HOME_BANNER_SECTION_2,
            key: Headings.HOME_SECTION2_MAIN_DESCRIPTION,
            content: data.cardSectionMainDescription,
            page: Page.HOME_PAGE,
          },
          // Card sections
          ...[1, 2, 3].flatMap((index) => [
            {
              section: Sections.HOME_BANNER_SECTION_2,
              key: `card${index}Title`,
              content: data[`card${index}Title`],
              page: Page.HOME_PAGE,
            },
            {
              section: Sections.HOME_BANNER_SECTION_2,
              key: `card${index}Description`,
              content: data[`card${index}Description`],
              page: Page.HOME_PAGE,
            },
            {
              section: Sections.HOME_BANNER_SECTION_2,
              key: `card${index}Icon`,
              content:
                CloudFrontURL +
                  signedUrls.find((url) => url.type === `card${index}Icon`)
                    ?.key || "",
              page: Page.HOME_PAGE,
            },
          ]),
          // Section 3
          {
            section: Sections.HOME_BANNER_SECTION_3,
            key: Headings.HOME_SECTION3_TITLE,
            content: data.sliderTitle,
            page: Page.HOME_PAGE,
          },
        ];

        // Upload images
        await Promise.all(
          signedUrls.map(async (signedUrl) => {
            if (signedUrl?.uploadUrl) {
              const imageObject = imagesToUpload.find(
                (image) => image.type === signedUrl.type
              );
              if (imageObject) {
                await uploadImage(imageObject.image, signedUrl.uploadUrl);
              }
            }
          })
        );

        const payload = {
          data: { sections: dataToUpload },
          url: `pages/${Page.HOME_PAGE}`,
          headerToken: localStorageUser.data.accessToken,
        };
        await apiService.post(payload);
        methods.reset();
        getHomePageData();

        toast.success("Data uploaded successfully");
      } catch (error: any) {
        console.error(error, "Error occurred while uploading images");
        toast.error(error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    } else {
      const nonStringImages = imagesToUpload.filter(
        (image) => typeof image.image !== "string"
      );

      try {
        setLoading(true);
        const signedUrls = await Promise.all(
          nonStringImages.map(async (imageObject) => {
            const { image } = imageObject;
            const imageKey = data[image];
            const imageInfo = {
              fileName: image.name,
              contentType: image.type,
            };
            const res = await getSignedUrl(imageInfo);
            return {
              type: imageObject.type,
              uploadUrl: res.data.uploadUrl,
              key: res.data.key,
            };
          })
        );

        // Prepare data to upload
        const dataToUpload: any[] = [
          // Banner section
          {
            id: homePageData.find(
              (item) => item.key === Headings.HOME_BANNER_TITLE
            )?.id,
            section: Sections.HOME_BANNER_SECTION_1,
            key: Headings.HOME_BANNER_TITLE,
            content: data.homeBannerTitle,
            page: Page.HOME_PAGE,
          },
          {
            id: homePageData.find(
              (item) => item.key === Headings.HOME_BANNER_DESCRIPTIION
            )?.id,
            section: Sections.HOME_BANNER_SECTION_1,
            key: Headings.HOME_BANNER_DESCRIPTIION,
            content: data.homeBannerDescription,
            page: Page.HOME_PAGE,
          },
          {
            id: homePageData.find(
              (item) => item.key === Headings.HOME_BANNER_IMAGE
            )?.id,
            section: Sections.HOME_BANNER_SECTION_1,
            key: Headings.HOME_BANNER_IMAGE,
            content: signedUrls?.find((url) => url.type === "homeBannerImage")
              ? CloudFrontURL +
                signedUrls?.find((url) => url.type === "homeBannerImage")?.key
              : homePageData.find(
                  (item) => item.key === Headings.HOME_BANNER_IMAGE
                )?.content || "",
            page: Page.HOME_PAGE,
          },

          // Section 2 title and description
          {
            id: homePageData.find(
              (item) => item.key === Headings.HOME_SECTION2_MAIN_TITLE
            )?.id,
            section: Sections.HOME_BANNER_SECTION_2,
            key: Headings.HOME_SECTION2_MAIN_TITLE,
            content: data.cardSectionMainTitle,
            page: Page.HOME_PAGE,
          },
          {
            id: homePageData.find(
              (item) => item.key === Headings.HOME_SECTION2_MAIN_DESCRIPTION
            )?.id,
            section: Sections.HOME_BANNER_SECTION_2,
            key: Headings.HOME_SECTION2_MAIN_DESCRIPTION,
            content: data.cardSectionMainDescription,
            page: Page.HOME_PAGE,
          },
          // Card sections
          ...[1, 2, 3].flatMap((index) => [
            {
              id: homePageData.find((item) => item.key === `card${index}Title`)
                ?.id,
              section: Sections.HOME_BANNER_SECTION_2,
              key: `card${index}Title`,
              content: data[`card${index}Title`],
              page: Page.HOME_PAGE,
            },
            {
              id: homePageData.find(
                (item) => item.key === `card${index}Description`
              )?.id,
              section: Sections.HOME_BANNER_SECTION_2,
              key: `card${index}Description`,
              content: data[`card${index}Description`],
              page: Page.HOME_PAGE,
            },
            {
              id: homePageData.find((item) => item.key === `card${index}Icon`)
                ?.id,
              section: Sections.HOME_BANNER_SECTION_2,
              key: `card${index}Icon`,
              content: signedUrls.find((url) => url.type === `card${index}Icon`)
                ? CloudFrontURL +
                  signedUrls?.find((url) => url.type === `card${index}Icon`)
                    ?.key
                : homePageData.find((item) => item.key === `card${index}Icon`)
                    ?.content || "",
              page: Page.HOME_PAGE,
            },
          ]),
          // Section 3
          {
            id: homePageData.find(
              (item) => item.key === Headings.HOME_SECTION3_TITLE
            )?.id,
            section: Sections.HOME_BANNER_SECTION_3,
            key: Headings.HOME_SECTION3_TITLE,
            content: data.sliderTitle,
            page: Page.HOME_PAGE,
          },
        ];

        await Promise.all(
          signedUrls.map(async (signedUrl) => {
            if (signedUrl?.uploadUrl) {
              const imageObject = imagesToUpload.find(
                (image) => image.type === signedUrl.type
              );
              if (imageObject) {
                await uploadImage(imageObject.image, signedUrl.uploadUrl);
              }
            }
          })
        );
        const payload = {
          data: { sections: dataToUpload },
          url: `pages/${Page.HOME_PAGE}`,
          headerToken: localStorageUser.data.accessToken,
        };
        await apiService.post(payload);
        getHomePageData();
        toast.success("Data updated successfully");
      } catch (error: any) {
        console.error(error, "Error occurred while uploading images");
        toast.error(error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePublish = async () => {
    setLoadingForPublish(true);
    try {
      const payload = {
        url: `publish/pages/${Page.HOME_PAGE}`,
        headerToken: localStorageUser.data.accessToken,
      };
      await apiService.post(payload);
      toast.success("Published successfully");
      getHomePageData();
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoadingForPublish(false);
    }
  };

  return (
    <div className={`${styles.home}`}>
      <div
        className={`flex align-center justify-between ${styles.home__header}`}
      >
        <h2>Home</h2>
        <div className={`flex align-center ${styles.home__button}`}>
          <Button
            label="Save"
            type="submit"
            buttonClass="md"
            loading={loading}
            disabled={loading || !isFormEdited}
            onClick={handleSubmit(handleSave)}
          />

          <Button
            label="Publish"
            type="submit"
            buttonClass="md"
            loading={loadingForPublish}
            disabled={isPublish}
            onClick={handlePublish}
          />
        </div>
      </div>

      <form
        onSubmit={(event: React.FormEvent<HTMLFormElement>) =>
          event.preventDefault()
        }
        noValidate
      >
        <div className={`flex-column ${styles.home__form}`}>
          <FormProvider {...methods}>
            <Accordion title="Home Banner ">
              <HomeBannerSection />
            </Accordion>
          </FormProvider>

          <FormProvider {...methods}>
            <Accordion title="Simplify Your Electrician Search">
              <HomeCardsSection />
            </Accordion>
          </FormProvider>

          <FormProvider {...methods}>
            <Accordion title="Explore a Spectrum of Services">
              <HomeSliderSection />
            </Accordion>
          </FormProvider>
        </div>
      </form>
      {loading && <GlobalLoader />}
    </div>
  );
};

export default Home;
